import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/components/DocumentThumbnail'
import { RevisionCardActions } from 'features/knowledgeDocumentViewer/components/metadata/revisionFamilyCardStyles'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { ExternalLink } from 'lucide-react'
import React, { useCallback } from 'react'

const REVISION_THUMBNAIL_MAX_REM = 8

function formatRevisionTitle(member: RevisionFamilyMemberView): string {
  const revisionCode = member.revisionCode?.trim()
  return revisionCode ? t`Revision ${revisionCode}` : t`Revision unknown`
}

/** Full + icon pair; `RevisionCardActions` shows one via container query. */
function OpenInNewTabButton({
  title,
  onClick,
}: {
  title: string
  onClick: () => void
}): React.ReactElement {
  const { t } = useLingui()
  const ariaLabel = t`Open ${title} in new tab`
  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        data-open-tab='full'
        aria-label={ariaLabel}
        onClick={onClick}>
        {t`Open Tab`}
        <ExternalLink data-icon='inline-end' />
      </Button>
      <Button
        type='button'
        variant='outline'
        size='icon-sm'
        data-open-tab='icon'
        aria-label={ariaLabel}
        onClick={onClick}>
        <ExternalLink />
      </Button>
    </>
  )
}

export type RevisionFamilyCardProps = {
  member: RevisionFamilyMemberView
  isLatest: boolean
  onSelectVersion?: (member: RevisionFamilyMemberView) => void
  onOpenInNewTab?: (member: RevisionFamilyMemberView) => void
}

export function RevisionFamilyCard({
  member,
  isLatest,
  onSelectVersion,
  onOpenInNewTab,
}: RevisionFamilyCardProps): React.ReactElement {
  const { t } = useLingui()
  const showActions =
    onSelectVersion !== undefined && onOpenInNewTab !== undefined

  const handleSelect = useCallback(() => {
    if (onSelectVersion === undefined || member.isCurrent) return
    onSelectVersion(member)
  }, [member, onSelectVersion])

  const handleOpen = useCallback(() => {
    if (onOpenInNewTab === undefined) return
    onOpenInNewTab(member)
  }, [member, onOpenInNewTab])

  const title = formatRevisionTitle(member)
  const statusLabel = isLatest ? t`Latest` : t`Out of Date`
  const selectLabel = member.isCurrent ? t`Selected` : t`Select Revision`

  const thumbnail = member.systemEntryId ? (
    <DocumentThumbnail
      documentId={member.systemEntryId}
      alt={title}
      filename={member.filename ?? undefined}
      bqDocumentId={member.bqDocumentId}
      cantileverDocumentEntryId={member.cantileverDocumentEntryId}
      documentHostsHydrated
      pageNumber={member.pageNum}
      maxDimensionRem={REVISION_THUMBNAIL_MAX_REM}
    />
  ) : (
    <Text variant='caption' color='muted'>
      {t`No preview`}
    </Text>
  )

  return (
    <Box
      display='flex'
      align='stretch'
      gap='md'
      padding='md'
      radius='xl'
      border='default'
      surface='muted'>
      <Box
        display='flex'
        align='center'
        justify='center'
        shrink={false}
        overflow='hidden'
        radius='sm'>
        {thumbnail}
      </Box>
      <Box
        display='flex'
        direction='column'
        justify='between'
        gap='sm'
        flex='one'
        minWidth='none'
        minHeight='none'>
        <Box display='flex' direction='column' gap='sm'>
          <Box display='flex' align='center' justify='between' gap='sm'>
            <Box flex='one' minWidth='none'>
              <Text variant='body' weight='semibold' truncate>
                {title}
              </Text>
            </Box>
            <Badge variant={isLatest ? 'outline' : 'outlineMuted'}>
              {statusLabel}
            </Badge>
          </Box>
          {member.revisionDate ? (
            <Text variant='caption' color='muted'>
              {member.revisionDate}
            </Text>
          ) : null}
        </Box>
        {showActions ? (
          <RevisionCardActions>
            <Button
              type='button'
              variant={member.isCurrent ? 'secondary' : 'default'}
              size='sm'
              disabled={member.isCurrent}
              aria-label={
                member.isCurrent ? t`${title} selected` : t`Select ${title}`
              }
              onClick={handleSelect}>
              {selectLabel}
            </Button>
            <OpenInNewTabButton title={title} onClick={handleOpen} />
          </RevisionCardActions>
        ) : null}
      </Box>
    </Box>
  )
}
